import { useState } from "react";
import { Zap, ChevronDown } from "lucide-react";
import { DocumentModal } from "~/components/DocumentModal";
import { Kbd } from "~/components/Kbd";
import { PopoverButton } from "~/components/assistant/PopoverButton";
import { SkillsPopup } from "~/components/assistant/SkillsPopup";
import type { PartialSkill } from "~/components/assistant/types";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

interface SkillSelectorProps {
  systemSkills: PartialSkill[];
  userSkills: PartialSkill[];
  onSelectSkill: (skillName: string) => void;
  onAddSkill?: () => void;
  onEditSkill?: (skillId: string) => void;
  onDeleteSkill?: (skillId: string) => void;
}

export function SkillSelector({
  systemSkills,
  userSkills,
  onSelectSkill,
  onAddSkill,
  onEditSkill,
  onDeleteSkill,
}: SkillSelectorProps) {
  const [open, setOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const handleSelectSkill = (skillName: string) => {
    onSelectSkill(skillName);
    setOpen(false);
  };

  return (
    <>
      <Tooltip delayDuration={700}>
        <Popover open={open} onOpenChange={setOpen}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <PopoverButton
                variant="ghost"
                className="has-[>svg]:px-2"
                aria-label="Select AI Skill"
              >
                <Zap className="size-4 text-gray-600" />
                <div className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-gray-700 text-[13px] text-left text-nowrap">
                  <p className="block leading-[16px] whitespace-pre">Skills</p>
                </div>
                <ChevronDown className="size-4 text-gray-400" />
              </PopoverButton>
            </PopoverTrigger>
          </TooltipTrigger>
          <PopoverContent className="w-84 p-0" align="start" side="bottom">
            <SkillsPopup
              systemSkills={systemSkills}
              userSkills={userSkills}
              onSelectSkill={handleSelectSkill}
              onAddSkill={onAddSkill}
              onEditSkill={onEditSkill}
              onDeleteSkill={onDeleteSkill}
              onLearnMore={() => setHelpModalOpen(true)}
              onClose={() => setOpen(false)}
            />
          </PopoverContent>
        </Popover>
        <TooltipContent className="max-w-[150px]">
          <div className="flex items-center gap-2">
            <p className="text-xs">Use AI skills</p>
            <Kbd>/</Kbd>
          </div>
        </TooltipContent>
      </Tooltip>

      <DocumentModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        title="AI Skills Guide"
        markdownPath="/assets/docs/agent/skills.md"
      />
    </>
  );
}
